import React, { useRef } from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { cn } from "@/lib/utils";
import { ImageScrollTextData } from "./types";
import SvgAnglesDown from "@/styles/icons/AnglesDown";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface DesktopImageScrollTextProps {
  data: ImageScrollTextData;
}

const scrollTextVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: 0.15 * i,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const logoVariants = {
  hidden: { x: -40, opacity: 0 },
  visible: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: 0.12 * i,
      duration: 0.5,
      ease: "easeOut",
    },
  }),
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function DesktopImageScrollText({
  data,
}: DesktopImageScrollTextProps) {
  const textScrollRef = useRef<HTMLDivElement>(null);
  const componentRef = useRef<HTMLElement>(null);

  const isRight = data.boxAligement === "right";

  const textSection = (
    <div
      className={cn(
        "flex-1",
        "max-w-none",
        "overflow-hidden",
        "h-full",
        "scroll-text-col",
        "relative",
        "ml-[3.33vw]",
        "mr-[7.5vw]",
        "w-[60vw]"
      )}
      style={{
        maxWidth: "60vw",
        marginLeft: "3.33vw",
        marginRight: "7.5vw",
      }}
    >
      <div
        data-lenis-prevent
        ref={textScrollRef}
        className={cn(
          "w-full flex flex-col justify-start overflow-y-auto",
          "h-full max-h-[calc(100svh-128px-128px-64px-64px)]",
          "scrollbar-white"
        )}
        style={{
          scrollBehavior: "smooth",
        }}
      >
        <div className={cn("flex flex-col gap-8 pr-6")}>
          {data.scrollText.map((block, i) => (
            <m.div
              key={i}
              className="mb-8"
              variants={fadeInVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2
                className={cn(
                  "bookmania-light text-h2-desktop text-accent-color-two-light mb-4"
                )}
              >
                {block.item.title}
              </h2>
              <div
                className={cn(
                  "sans-light text-gray-white text-lg leading-relaxed",
                  "[&_*:is(a)]:underline",
                  "[&_*:is(ul)]:list-disc [&_*:is(ul)]:pl-6",
                  "[&_*:is(ol)]:list-decimal [&_*:is(ol)]:pl-6",
                  "scrollbar-white"
                )}
                dangerouslySetInnerHTML={{ __html: block.item.text }}
              ></div>
            </m.div>
          ))}
          <div className="h-32" />
        </div>
      </div>
      <span
        className="flex gap-6 text-white w-full p-6 justify-end text-span-medium cursor-pointer"
        onClick={() => {
          const el = textScrollRef.current;
          if (el) {
            el.scrollBy({ top: 200, behavior: "smooth" }); // scrolla 200px prema dolje
          }
        }}
      >
        SCROLL <SvgAnglesDown className="text-secondary" />
      </span>
    </div>
  );

  const imageSection = (
    <div
      className={cn(
        "relative self-center flex-1 h-full ml-[3.33vw] max-w-[35vw]"
      )}
    >
      <Image
        src={getIconUrl(data.mainImage)}
        alt="Background"
        fill
        className={cn("pointer-events-none select-none", "object-contain")}
        priority={false}
        draggable={false}
      />
    </div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <section
      ref={componentRef}
      className={cn("bg-accent-color-three-dark h-svh relative")}
    >
      <div className={cn("w-full h-full flex gap-[3.33vw] pt-24 pb-8")}>
        {isRight ? (
          <>
            {imageSection}
            {textSection}
          </>
        ) : (
          <>
            {textSection}
            {imageSection}
          </>
        )}
      </div>

      {/* Desktop Logos */}
      <div
        className={cn(
          "absolute flex flex-wrap gap-8 justify-between items-center bg-accent-color-three-light p-4 mt-8 z-10 w-fit right-0 bottom-16",
          "pr-32 pl-12"
        )}
        style={{
          width: "70vw",
        }}
      >
        {data.logos.map((logoUrl, i) => (
          <m.div
            key={i}
            variants={fadeInVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <Image
              src={getIconUrl(logoUrl.directus_files_id)}
              alt={`logo-${i}`}
              width={80}
              height={40}
              className={cn(
                "object-contain",
                "grayscale",
                "invert",
                "brightness-0"
              )}
            />
          </m.div>
        ))}
      </div>
    </section>
    </LazyMotion>
  );
}
